<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import * as yup from 'yup';
import * as rutHelpers from 'rut-helpers';
import { usersApi, type SerializedUser, type UserIdentity } from '@/api/users';
import { requestErrorMessage } from '@/utils/api-errors';
import {
  authorIdentityCancelled,
  authorIdentityPrompt,
  authorIdentitySaved,
} from '@/utils/author-identity';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// Shown once for the whole app when a clinical write comes back
// author_identity_required: the user gives their name and RUT, which become
// the Autor of their registros clínicos, and the write is sent again.
const { t } = useI18n();
const currentUser = inject<Ref<SerializedUser | undefined> | undefined>('currentUser', undefined);
const setCurrentUser = inject<((user: SerializedUser) => void) | undefined>('setCurrentUser', undefined);

const { errors, defineField, handleSubmit, resetForm } = useForm<UserIdentity>({
  validationSchema: yup.object({
    firstName: yup.string().trim().required(t('userIdentity.fieldRequired')),
    lastName: yup.string().trim().required(t('userIdentity.fieldRequired')),
    dni: yup.string().required(t('userIdentity.fieldRequired'))
      .test('valid-dni', t('userIdentity.invalidDni'), (value) => !!value && rutHelpers.rutValidate(value)),
  }),
});

const [firstName, firstNameAttrs] = defineField('firstName');
const [lastName, lastNameAttrs] = defineField('lastName');
const [dni, dniAttrs] = defineField('dni');
const saveError = ref('');

watch(dni, (value) => {
  if (value) dni.value = rutHelpers.rutFormat(value);
});

// What the user already gave, so they only complete what is missing.
function knownIdentity(user: SerializedUser | undefined): UserIdentity {
  const known = { firstName: '', lastName: '', ...user };

  return {
    firstName: known.firstName ?? '', lastName: known.lastName ?? '',
    dni: known.dni ? rutHelpers.rutFormat(known.dni) : '',
  };
}

watch(() => authorIdentityPrompt.open, (open) => {
  if (!open) return;

  saveError.value = '';
  resetForm({ values: knownIdentity(currentUser?.value) });
});

const save = useMutation({
  mutationFn: (identity: UserIdentity) => usersApi.updateProfile(identity),
  onSuccess: (user) => {
    setCurrentUser?.(user);
    authorIdentitySaved();
  },
  onError: (error) => {
    saveError.value = requestErrorMessage(error, t('userIdentity.saveError'));
  },
});

const onSubmit = handleSubmit((values) => save.mutate(values));

function onModelUpdate(open: boolean) {
  if (!open) authorIdentityCancelled();
}

onMounted(() => {
  authorIdentityPrompt.mounted += 1;
});

onBeforeUnmount(() => {
  authorIdentityPrompt.mounted -= 1;
  if (authorIdentityPrompt.open) authorIdentityCancelled();
});
</script>
<template>
  <responsive-dialog
    :model-value="authorIdentityPrompt.open"
    :title="t('userIdentity.title')"
    max-width="520"
    @update:model-value="onModelUpdate"
    @close="authorIdentityCancelled"
  >
    <form
      id="author-identity-form"
      class="d-flex flex-column ga-3 pt-2"
      @submit.prevent="onSubmit"
    >
      <p class="text-body-2">
        {{ t('userIdentity.intro') }}
      </p>
      <v-text-field
        v-model="firstName"
        v-bind="firstNameAttrs"
        :label="t('userIdentity.firstName')"
        :error-messages="errors.firstName"
        autocomplete="given-name"
        variant="outlined"
      />
      <v-text-field
        v-model="lastName"
        v-bind="lastNameAttrs"
        :label="t('userIdentity.lastName')"
        :error-messages="errors.lastName"
        autocomplete="family-name"
        variant="outlined"
      />
      <v-text-field
        v-model="dni"
        v-bind="dniAttrs"
        :label="t('userIdentity.dni')"
        :error-messages="errors.dni"
        variant="outlined"
      />
      <v-alert
        v-if="saveError"
        type="error"
        variant="tonal"
        :text="saveError"
      />
    </form>
    <template #actions>
      <v-btn
        :disabled="save.isPending.value"
        @click="authorIdentityCancelled"
      >
        {{ t('userIdentity.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        type="submit"
        form="author-identity-form"
        :loading="save.isPending.value"
      >
        {{ t('userIdentity.save') }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>
